import { GitBranch, GitCommit, Calendar, Clock, MessageSquare, Copy, Check } from 'lucide-react';
import { useState } from 'react';

// Git information injected at build time by Vite
const gitInfo = {
    commitHash: typeof __GIT_COMMIT_HASH__ !== 'undefined' ? __GIT_COMMIT_HASH__ : 'unknown',
    commitHashShort: typeof __GIT_COMMIT_HASH_SHORT__ !== 'undefined' ? __GIT_COMMIT_HASH_SHORT__ : 'unknown',
    branch: typeof __GIT_BRANCH__ !== 'undefined' ? __GIT_BRANCH__ : 'unknown',
    commitDate: typeof __GIT_COMMIT_DATE__ !== 'undefined' ? __GIT_COMMIT_DATE__ : 'unknown',
    commitMessage: typeof __GIT_COMMIT_MESSAGE__ !== 'undefined' ? __GIT_COMMIT_MESSAGE__ : 'unknown',
    buildTime: typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : 'unknown'
};

function formatDate(dateString) {
    if (dateString === 'unknown') return 'Unknown';
    try {
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZoneName: 'short'
        });
    } catch {
        return dateString;
    }
}

function formatRelativeTime(dateString) {
    if (dateString === 'unknown') return '';
    try {
        const date = new Date(dateString);
        const now = new Date();
        const diff = now - date;
        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);

        if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
        if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
        if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
        return 'Just now';
    } catch {
        return '';
    }
}

export default function VersionPage() {
    const [copied, setCopied] = useState(false);

    const handleCopyVersion = async () => {
        const versionString = `${gitInfo.branch}@${gitInfo.commitHashShort}`;
        try {
            await navigator.clipboard.writeText(versionString);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    return (
        <div className="py-6">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="bg-white shadow rounded-lg mb-6 p-6">
                    <div className="flex justify-between items-start mb-2">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">Version Information</h1>
                            <p className="text-gray-500 mt-1">Current deployment details and Git information</p>
                        </div>
                        <button
                            onClick={handleCopyVersion}
                            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors text-sm font-medium"
                        >
                            {copied ? (
                                <>
                                    <Check size={16} className="text-green-600" />
                                    <span className="text-green-600">Copied!</span>
                                </>
                            ) : (
                                <>
                                    <Copy size={16} />
                                    <span>Copy Version</span>
                                </>
                            )}
                        </button>
                    </div>

                    {/* Quick Version Badge */}
                    <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-orange-50 border border-orange-200 rounded-full">
                        <GitCommit size={16} className="text-orange-600" />
                        <code className="text-sm font-mono font-semibold text-orange-700">
                            {gitInfo.branch}@{gitInfo.commitHashShort}
                        </code>
                    </div>
                </div>

                {/* Info Cards Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    {/* Branch Card */}
                    <div className="bg-white shadow rounded-lg p-6">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg">
                                <GitBranch size={20} className="text-blue-600" />
                            </div>
                            <span className="text-sm font-medium text-gray-500 uppercase tracking-wide">Branch</span>
                        </div>
                        <p className="text-2xl font-bold text-gray-900">{gitInfo.branch}</p>
                    </div>

                    {/* Commit Hash Card */}
                    <div className="bg-white shadow rounded-lg p-6">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="flex items-center justify-center w-10 h-10 bg-purple-100 rounded-lg">
                                <GitCommit size={20} className="text-purple-600" />
                            </div>
                            <span className="text-sm font-medium text-gray-500 uppercase tracking-wide">Commit</span>
                        </div>
                        <p className="text-2xl font-bold font-mono text-gray-900">{gitInfo.commitHashShort}</p>
                        <p className="text-xs font-mono text-gray-400 mt-1 break-all">{gitInfo.commitHash}</p>
                    </div>

                    {/* Commit Date Card */}
                    <div className="bg-white shadow rounded-lg p-6">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-lg">
                                <Calendar size={20} className="text-green-600" />
                            </div>
                            <span className="text-sm font-medium text-gray-500 uppercase tracking-wide">Last Commit</span>
                        </div>
                        <p className="text-lg font-semibold text-gray-900">{formatDate(gitInfo.commitDate)}</p>
                        {formatRelativeTime(gitInfo.commitDate) && (
                            <p className="text-sm text-gray-500 mt-1">{formatRelativeTime(gitInfo.commitDate)}</p>
                        )}
                    </div>

                    {/* Build Time Card */}
                    <div className="bg-white shadow rounded-lg p-6">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="flex items-center justify-center w-10 h-10 bg-amber-100 rounded-lg">
                                <Clock size={20} className="text-amber-600" />
                            </div>
                            <span className="text-sm font-medium text-gray-500 uppercase tracking-wide">Build Time</span>
                        </div>
                        <p className="text-lg font-semibold text-gray-900">{formatDate(gitInfo.buildTime)}</p>
                        {formatRelativeTime(gitInfo.buildTime) && (
                            <p className="text-sm text-gray-500 mt-1">{formatRelativeTime(gitInfo.buildTime)}</p>
                        )}
                    </div>
                </div>

                {/* Commit Message Card */}
                <div className="bg-white shadow rounded-lg p-6 mb-6">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="flex items-center justify-center w-10 h-10 bg-indigo-100 rounded-lg">
                            <MessageSquare size={20} className="text-indigo-600" />
                        </div>
                        <span className="text-sm font-medium text-gray-500 uppercase tracking-wide">Last Commit Message</span>
                    </div>
                    <p className="text-gray-900 text-lg leading-relaxed">{gitInfo.commitMessage}</p>
                </div>

                {/* Help Section */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-blue-900 mb-2">ℹ️ About Version Information</h3>
                    <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                        <li>Version info is captured at build time from Git</li>
                        <li>After pulling changes, rebuild the app to see updated version</li>
                        <li>Use the version string to verify deployments match expected commits</li>
                        <li>Compare commit hash with <code className="bg-blue-100 px-1 rounded">git log --oneline -1</code> to verify</li>
                    </ul>
                </div>
            </div>
        </div>
    );
}
